import { getFormattedNumber } from '@vestigas/utils';

/** The API's default receiver timeout */
export const API_DEFAULT_TIMEOUT_S = 30;

const API_MIN_TIMEOUT_S = 1;
const API_MAX_TIMEOUT_S = 60;

export const getApiTimeoutBounds = (): { max: string; min: string } => ({
  max: getFormattedNumber({ digits: 0, value: API_MAX_TIMEOUT_S }),
  min: getFormattedNumber({ digits: 0, value: API_MIN_TIMEOUT_S }),
});

/** `link` adds a download link for the delivery note PDF */
export const API_PDF_MODES = ['none', 'link'] as const;

export type ApiPdfMode = (typeof API_PDF_MODES)[number];

export type ApiHeaderEntry = {
  /** Stable React key, not sent to the API */
  id: string;
  name: string;
  value: string;
};

/** Standard Webhooks secret prefix */
const SIGNING_SECRET_PREFIX = 'whsec_';

const SIGNING_SECRET_MIN_BYTES = 24;
const SIGNING_SECRET_MAX_BYTES = 64;
const GENERATED_SECRET_BYTES = 32;

/** RFC 9110 token */
const HTTP_HEADER_NAME = /^[!#$%&'*+.^_`|~0-9a-z-]+$/i;

/** Printable ASCII and tabs, as the API accepts */
const HTTP_HEADER_VALUE = /^[\t\x20-\x7E]*$/;

/** Set by the API on every request, so not configurable */
const RESERVED_HEADER_NAMES: ReadonlySet<string> = new Set([
  'connection',
  'content-length',
  'content-type',
  'host',
  'transfer-encoding',
  'user-agent',
  'webhook-id',
  'webhook-signature',
  'webhook-timestamp',
]);
const RESERVED_HEADER_PREFIX = 'x-vestigas-';

/** Belong into the auth header, static headers are returned by the API */
const CREDENTIAL_HEADER_NAMES: ReadonlySet<string> = new Set([
  'api-key',
  'authorization',
  'cookie',
  'proxy-authorization',
  'x-api-key',
]);

/** https only, no credentials, fragment or localhost; private IPs are left to the API */
export const isApiUrl = (value: string) => {
  const trimmed = value.trim();
  let url: URL;
  try {
    url = new URL(trimmed);
  } catch {
    return false;
  }

  const host = url.hostname.toLowerCase().replace(/\.$/, '');

  return (
    url.protocol === 'https:' &&
    !url.username &&
    !url.password &&
    !/[\s#]/.test(trimmed) &&
    host !== 'localhost' &&
    !host.endsWith('.localhost')
  );
};

const isHttpHeaderName = (value: string) => HTTP_HEADER_NAME.test(value);

/** No umlauts, no line breaks that would split the header */
export const isHttpHeaderValue = (value: string) =>
  HTTP_HEADER_VALUE.test(value);

const isReservedApiHeaderName = (name: string) =>
  RESERVED_HEADER_NAMES.has(name) || name.startsWith(RESERVED_HEADER_PREFIX);

export type ApiHeaderNameProblem = 'credential' | 'invalid' | 'reserved';

/** The auth header may carry a credential, it is never returned */
export const getApiHeaderNameProblem = (
  value: string,
  isAuthHeader: boolean,
): ApiHeaderNameProblem | undefined => {
  if (!isHttpHeaderName(value)) {
    return 'invalid';
  }

  const name = value.toLowerCase();

  if (isReservedApiHeaderName(name)) {
    return 'reserved';
  }

  return !isAuthHeader && CREDENTIAL_HEADER_NAMES.has(name)
    ? 'credential'
    : undefined;
};

export const isApiTimeoutInput = (value: string) =>
  /^\d{1,2}$/.test(value) &&
  Number(value) >= API_MIN_TIMEOUT_S &&
  Number(value) <= API_MAX_TIMEOUT_S;

/** Padded base64, which atob alone does not check */
const BASE64 =
  /^(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$/;

const decodedLength = (base64: string) =>
  BASE64.test(base64) ? atob(base64).length : 0;

/** `whsec_` + base64 of a 24 to 64 byte key */
export const isApiSigningSecret = (value: string) => {
  if (!value.startsWith(SIGNING_SECRET_PREFIX)) {
    return false;
  }

  const length = decodedLength(value.slice(SIGNING_SECRET_PREFIX.length));

  return (
    length >= SIGNING_SECRET_MIN_BYTES && length <= SIGNING_SECRET_MAX_BYTES
  );
};

export const generateApiSigningSecret = () => {
  const bytes = crypto.getRandomValues(new Uint8Array(GENERATED_SECRET_BYTES));

  return `${SIGNING_SECRET_PREFIX}${btoa(String.fromCodePoint(...bytes))}`;
};

export const toApiHeaderEntries = (
  headers: Record<string, string> | null | undefined,
): ApiHeaderEntry[] =>
  Object.entries(headers ?? {}).map(([name, value]) => ({
    id: crypto.randomUUID(),
    name,
    value,
  }));

/** No rows clears the headers (null) */
export const fromApiHeaderEntries = (entries: ApiHeaderEntry[]) =>
  entries.length === 0
    ? null
    : Object.fromEntries(entries.map(({ name, value }) => [name, value]));

/** Header names are case insensitive, so `x-key` and `X-Key` collide */
export const getDuplicateApiHeaderNames = (
  entries: ReadonlyArray<Pick<ApiHeaderEntry, 'name'>>,
  authHeaderName: string,
) => {
  const counts = new Map<string, number>();
  for (const name of [...entries.map((entry) => entry.name), authHeaderName]) {
    if (name) {
      const key = name.toLowerCase();
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }
  }

  return new Set(
    [...counts].filter(([, count]) => count > 1).map(([name]) => name),
  );
};
